import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearch } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  BarChart3,
  Building2,
  CheckCircle2,
  Download,
  Eye,
  Image,
  RefreshCw,
  Search,
} from 'lucide-react';
import {
  fetchPortalCompliance,
  fetchPortalContext,
  fetchPortalCycles,
  fetchAllPortalCampaigns,
  fetchPortalGallery,
} from '@/lib/portalApi';
import {
  buildPortalComplianceCampaignCsv,
  buildPortalComplianceCsv,
  buildPortalComplianceStoreCsv,
  buildPortalComplianceWorkspace,
  type PortalComplianceCampaignRow,
  type PortalComplianceStatus,
  type PortalComplianceStoreRow,
  type PortalComplianceTaskRow,
} from '@/lib/portalComplianceWorkspace';
import { PortalShell } from './PortalShell';
import {
  PortalCyclePickerFields,
  PortalErrorBlock,
  PortalInlineQueryError,
  PortalLoadingBlock,
  portalCardClass,
  portalPrimaryButtonClass,
  portalTableClass,
  portalTableHeadClass,
} from './portalUi';
import { buildPortalHref, parsePortalPositiveId } from './portalCycleQuery';
import { usePortalCycleSelection } from './usePortalCycleSelection';

type ComplianceView = 'campaign' | 'store' | 'task';
type SortDirection = 'asc' | 'desc';

const PAGE_SIZE = 25;

function queryErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function statusLabel(status: PortalComplianceStatus): string {
  if (status === 'not_started') return 'Not started';
  return status === 'complete' ? 'Complete' : 'Partial';
}

function StatusBadge({ status }: { status: PortalComplianceStatus }) {
  const className = status === 'complete'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
    : status === 'partial'
      ? 'border-amber-200 bg-amber-50 text-amber-700'
      : 'border-border bg-muted text-muted-foreground';
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${className}`}>
      {statusLabel(status)}
    </span>
  );
}

function SummaryCard({
  label,
  value,
  detail,
  icon,
  tone = 'default',
}: {
  label: string;
  value: string | number;
  detail: string;
  icon: React.ReactNode;
  tone?: 'default' | 'success' | 'warning';
}) {
  const iconClass = tone === 'success'
    ? 'bg-emerald-50 text-emerald-600'
    : tone === 'warning'
      ? 'bg-amber-50 text-amber-600'
      : 'bg-primary/10 text-primary';
  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
          <p className="mt-1 text-2xl font-semibold text-foreground">{value}</p>
          <p className="mt-1 text-xs text-muted-foreground">{detail}</p>
        </div>
        <span className={`rounded-lg p-2 ${iconClass}`}>{icon}</span>
      </div>
    </div>
  );
}

function SortButton({
  label,
  column,
  sortColumn,
  sortDirection,
  onSort,
}: {
  label: string;
  column: string;
  sortColumn: string;
  sortDirection: SortDirection;
  onSort: (column: string) => void;
}) {
  const active = column === sortColumn;
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 font-medium hover:text-foreground"
      onClick={() => onSort(column)}
    >
      {label}
      <span aria-hidden="true" className={active ? 'text-primary' : 'text-muted-foreground/50'}>
        {active ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </button>
  );
}

function Pagination({ page, totalCount, onPageChange }: {
  page: number;
  totalCount: number;
  onPageChange: (page: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const start = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, totalCount);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3 text-sm text-muted-foreground">
      <span>Showing {start}–{end} of {totalCount}</span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="rounded border border-border px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </button>
        <span>Page {page} of {totalPages}</span>
        <button
          type="button"
          className="rounded border border-border px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function compareValues(left: string | number, right: string | number): number {
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
}

function sortRows<T>(
  rows: T[],
  valueFor: (row: T) => string | number,
  direction: SortDirection,
): T[] {
  const multiplier = direction === 'asc' ? 1 : -1;
  return [...rows].sort((left, right) => compareValues(valueFor(left), valueFor(right)) * multiplier);
}

function includesSearch(values: Array<string | number | null>, search: string): boolean {
  const needle = search.trim().toLocaleLowerCase();
  if (!needle) return true;
  return values.some((value) => String(value ?? '').toLocaleLowerCase().includes(needle));
}

function downloadCsv(csv: string, cycleName: string): void {
  const safeCycleName = cycleName.toLocaleLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `reactor-compliance-${safeCycleName || 'cycle'}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function PortalCompliancePage() {
  const [pathname, setLocation] = useLocation();
  const urlSearch = useSearch();
  const { data: context, isLoading: contextLoading, isError: contextError, error: contextErrorDetail } = useQuery({
    queryKey: ['portal', 'context'],
    queryFn: fetchPortalContext,
  });

  const {
    data: cyclesData,
    isLoading: cyclesLoading,
    isError: cyclesError,
    error: cyclesErrorDetail,
  } = useQuery({
    queryKey: ['portal', 'cycles'],
    queryFn: fetchPortalCycles,
    enabled: !!context?.features.compliance,
  });

  const { selectedCycleId, onCycleChange } = usePortalCycleSelection(cyclesData?.items);
  const urlCampaignId = parsePortalPositiveId(urlSearch, 'campaignId');
  const selectedCampaignId: number | '' = urlCampaignId ?? '';

  const setSelectedCampaignId = (value: number | '') => {
    setLocation(buildPortalHref(pathname, urlSearch, {
      campaignId: value === '' ? null : value,
    }), { replace: false });
  };

  const [view, setView] = useState<ComplianceView>('task');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<PortalComplianceStatus | 'all'>('all');
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(1);

  const {
    data: campaignOptions = [],
    isLoading: campaignsLoading,
    isError: campaignsError,
    error: campaignsErrorDetail,
  } = useQuery({
    queryKey: ['portal', 'campaigns', selectedCycleId, 'compliance-filter-all'],
    queryFn: () => fetchAllPortalCampaigns(selectedCycleId!),
    enabled: selectedCycleId != null && !!context?.features.compliance,
  });

  const {
    data: compliance,
    isLoading: complianceLoading,
    isError: complianceError,
    error: complianceErrorDetail,
    refetch: refetchCompliance,
    isFetching: complianceFetching,
  } = useQuery({
    queryKey: ['portal', 'compliance', selectedCycleId, selectedCampaignId],
    queryFn: () => fetchPortalCompliance({
      cycleId: selectedCycleId!,
      campaignId: selectedCampaignId === '' ? undefined : selectedCampaignId,
    }),
    enabled: selectedCycleId != null,
  });

  const {
    data: gallerySummary,
    refetch: refetchGallery,
    isFetching: galleryFetching,
  } = useQuery({
    queryKey: ['portal', 'gallery-summary', selectedCycleId, selectedCampaignId],
    queryFn: () => fetchPortalGallery({
      cycleId: selectedCycleId!,
      campaignId: selectedCampaignId === '' ? undefined : selectedCampaignId,
      page: 1,
      pageSize: 1,
    }),
    enabled: selectedCycleId != null && !!context?.features.gallery,
  });

  useEffect(() => {
    setPage(1);
  }, [search, selectedCampaignId, selectedCycleId, statusFilter, view]);

  useEffect(() => {
    if (urlCampaignId == null || campaignOptions.length === 0) return;
    if (!campaignOptions.some((campaign) => campaign.id === urlCampaignId)) {
      setLocation(buildPortalHref(pathname, urlSearch, { campaignId: null }), { replace: true });
    }
  }, [urlCampaignId, campaignOptions, pathname, urlSearch, setLocation]);

  useEffect(() => {
    setSortColumn('name');
    setSortDirection('asc');
  }, [view]);

  const workspace = useMemo(
    () => compliance ? buildPortalComplianceWorkspace(compliance) : null,
    [compliance],
  );

  const filteredTasks = useMemo(() => {
    if (!workspace) return [];
    return workspace.tasks.filter((row) =>
      (statusFilter === 'all' || row.status === statusFilter)
      && includesSearch(
        [row.storeName, row.storeNumber, row.campaignName, row.formatName],
        search,
      ));
  }, [search, statusFilter, workspace]);

  const filteredStores = useMemo(() => {
    if (!workspace) return [];
    return workspace.stores.filter((row) =>
      (statusFilter === 'all' || row.status === statusFilter)
      && includesSearch([row.storeName, row.storeNumber], search));
  }, [search, statusFilter, workspace]);

  const filteredCampaigns = useMemo(() => {
    if (!workspace) return [];
    return workspace.campaigns.filter((row) =>
      (statusFilter === 'all' || row.status === statusFilter)
      && includesSearch([row.campaignName], search));
  }, [search, statusFilter, workspace]);

  const sortedTasks = useMemo(() => sortRows(filteredTasks, (row) => {
    if (sortColumn === 'campaign') return row.campaignName;
    if (sortColumn === 'format') return row.formatName;
    if (sortColumn === 'expected') return row.expectedQty;
    if (sortColumn === 'installed') return row.actualQty;
    if (sortColumn === 'shortfall') return row.shortfallQty;
    if (sortColumn === 'status') return row.status;
    return row.storeName;
  }, sortDirection), [filteredTasks, sortColumn, sortDirection]);

  const sortedStores = useMemo(() => sortRows(filteredStores, (row) => {
    if (sortColumn === 'campaigns') return row.campaignCount;
    if (sortColumn === 'lines') return row.completedLineCount / Math.max(1, row.lineCount);
    if (sortColumn === 'expected') return row.expectedQty;
    if (sortColumn === 'installed') return row.actualQty;
    if (sortColumn === 'shortfall') return row.shortfallQty;
    if (sortColumn === 'status') return row.status;
    return row.storeName;
  }, sortDirection), [filteredStores, sortColumn, sortDirection]);

  const sortedCampaigns = useMemo(() => sortRows(filteredCampaigns, (row) => {
    if (sortColumn === 'stores') return row.totalStoreCount;
    if (sortColumn === 'complete') return row.storesWithAllInstalled;
    if (sortColumn === 'progress') return row.completionPercent;
    if (sortColumn === 'expected') return row.expectedQty;
    if (sortColumn === 'installed') return row.actualQty;
    if (sortColumn === 'shortfall') return row.shortfallQty;
    return row.campaignName;
  }, sortDirection), [filteredCampaigns, sortColumn, sortDirection]);

  const activeCount = view === 'task'
    ? sortedTasks.length
    : view === 'store'
      ? sortedStores.length
      : sortedCampaigns.length;
  const pageStart = (page - 1) * PAGE_SIZE;
  const pagedTasks = sortedTasks.slice(pageStart, pageStart + PAGE_SIZE);
  const pagedStores = sortedStores.slice(pageStart, pageStart + PAGE_SIZE);
  const pagedCampaigns = sortedCampaigns.slice(pageStart, pageStart + PAGE_SIZE);

  const activeCsv = view === 'task'
    ? buildPortalComplianceCsv(sortedTasks)
    : view === 'store'
      ? buildPortalComplianceStoreCsv(sortedStores)
      : buildPortalComplianceCampaignCsv(sortedCampaigns);

  const handleSort = (column: string) => {
    if (column === sortColumn) {
      setSortDirection((current) => current === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setPage(1);
  };

  if (contextLoading) return <PortalLoadingBlock />;
  if (contextError || !context) {
    return <PortalErrorBlock message={queryErrorMessage(contextErrorDetail, 'Could not load portal context.')} />;
  }

  if (!context.features.compliance) {
    return (
      <PortalShell context={context}>
        <PortalErrorBlock message="Compliance summary is not enabled for your account." />
      </PortalShell>
    );
  }

  const selectedCycle = cyclesData?.items.find((cycle) => cycle.id === selectedCycleId);
  const isRefreshing = complianceFetching || galleryFetching;

  return (
    <PortalShell context={context}>
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-foreground">Compliance</h2>
            <p className="text-sm text-muted-foreground">
              Read-only installation status and approved evidence for {compliance?.retailerName ?? context.allowedRetailers[0]?.name}.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded border border-border bg-card px-3 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
              disabled={!compliance || isRefreshing}
              onClick={() => void Promise.all([
                refetchCompliance(),
                ...(context.features.gallery ? [refetchGallery()] : []),
              ])}
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              type="button"
              className={`inline-flex items-center gap-2 ${portalPrimaryButtonClass} disabled:cursor-not-allowed disabled:opacity-50`}
              disabled={!workspace || activeCount === 0}
              onClick={() => {
                if (!selectedCycle) return;
                downloadCsv(activeCsv, selectedCycle.name);
              }}
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-start gap-3 rounded-lg border border-border bg-card p-4 shadow-sm">
          <PortalCyclePickerFields
            cycles={cyclesData?.items ?? []}
            selectedCycleId={selectedCycleId}
            onCycleChange={onCycleChange}
            externalSelection
            disabled={cyclesLoading || cyclesError}
            errorMessage={cyclesError
              ? queryErrorMessage(cyclesErrorDetail, 'Could not load cycles.')
              : undefined}
          />
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Campaign</span>
            <select
              className="min-w-[240px] rounded border border-border bg-background px-3 py-2"
              value={selectedCampaignId}
              disabled={campaignsLoading || campaignsError || selectedCycleId == null}
              onChange={(event) => {
                const value = event.target.value;
                setSelectedCampaignId(value === '' ? '' : Number(value));
              }}
            >
              <option value="">All campaigns</option>
              {campaignOptions.map((campaign) => (
                <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
              ))}
            </select>
            {campaignsError ? (
              <PortalInlineQueryError
                message={queryErrorMessage(campaignsErrorDetail, 'Could not load campaigns.')}
              />
            ) : null}
          </label>
        </div>

        {complianceLoading ? (
          <PortalLoadingBlock />
        ) : complianceError || !compliance || !workspace ? (
          <PortalErrorBlock message={queryErrorMessage(complianceErrorDetail, 'Could not load compliance summary.')} />
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
              <SummaryCard
                label="Stores complete"
                value={`${workspace.summary.completeStores}/${workspace.summary.totalStores}`}
                detail={`${workspace.summary.totalStores === 0 ? 0 : Math.round((workspace.summary.completeStores / workspace.summary.totalStores) * 100)}% fully installed`}
                icon={<CheckCircle2 className="h-5 w-5" />}
                tone="success"
              />
              <SummaryCard
                label="Install lines"
                value={`${workspace.summary.completeLineCount}/${workspace.summary.totalLineCount}`}
                detail="Formats fully installed"
                icon={<BarChart3 className="h-5 w-5" />}
              />
              <SummaryCard
                label="Installed"
                value={`${workspace.summary.actualQty}/${workspace.summary.expectedQty}`}
                detail="Units installed versus due"
                icon={<Building2 className="h-5 w-5" />}
              />
              <SummaryCard
                label="Shortfall"
                value={workspace.summary.shortfallQty}
                detail="Units still outstanding"
                icon={<AlertTriangle className="h-5 w-5" />}
                tone={workspace.summary.shortfallQty > 0 ? 'warning' : 'success'}
              />
              <SummaryCard
                label="Approved evidence"
                value={gallerySummary?.totalCount ?? '—'}
                detail={`Updated ${new Date(compliance.asOf).toLocaleString()}`}
                icon={<Image className="h-5 w-5" />}
              />
            </div>

            <section className={portalCardClass}>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
                <div className="flex rounded-lg bg-muted p-1" role="tablist" aria-label="Compliance view">
                  {([
                    ['campaign', 'By campaign'],
                    ['store', 'By store'],
                    ['task', 'By task'],
                  ] as Array<[ComplianceView, string]>).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      role="tab"
                      aria-selected={view === value}
                      className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                        view === value ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                      }`}
                      onClick={() => setView(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
                  <label className="relative min-w-[240px] max-w-sm flex-1">
                    <span className="sr-only">Search compliance</span>
                    <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <input
                      type="search"
                      className="w-full rounded border border-border bg-background py-2 pl-9 pr-3 text-sm"
                      placeholder={view === 'campaign' ? 'Search campaigns…' : 'Search stores, campaigns, formats…'}
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                    />
                  </label>
                  <select
                    className="rounded border border-border bg-background px-3 py-2 text-sm"
                    value={statusFilter}
                    aria-label="Filter by status"
                    onChange={(event) => setStatusFilter(event.target.value as PortalComplianceStatus | 'all')}
                  >
                    <option value="all">All statuses</option>
                    <option value="complete">Complete</option>
                    <option value="partial">Partial</option>
                    <option value="not_started">Not started</option>
                  </select>
                </div>
              </div>

              <div className="overflow-x-auto">
                {view === 'campaign' ? (
                  <CampaignTable
                    rows={pagedCampaigns}
                    cycleId={compliance.cycleId}
                    galleryEnabled={context.features.gallery}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                ) : view === 'store' ? (
                  <StoreTable
                    rows={pagedStores}
                    cycleId={compliance.cycleId}
                    campaignId={selectedCampaignId === '' ? undefined : selectedCampaignId}
                    galleryEnabled={context.features.gallery}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                ) : (
                  <TaskTable
                    rows={pagedTasks}
                    cycleId={compliance.cycleId}
                    galleryEnabled={context.features.gallery}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                )}
              </div>

              {activeCount === 0 ? (
                <div className="border-t border-border px-4 py-10 text-center text-sm text-muted-foreground">
                  No compliance rows match these filters.
                </div>
              ) : null}
              <Pagination page={page} totalCount={activeCount} onPageChange={setPage} />
            </section>
          </>
        )}
      </div>
    </PortalShell>
  );
}

interface TableSortProps {
  sortColumn: string;
  sortDirection: SortDirection;
  onSort: (column: string) => void;
}

function EvidenceLink({ href }: { href: string }) {
  return (
    <Link
      href={href}
      className="inline-flex items-center gap-1 rounded border border-border px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
    >
      <Eye className="h-3.5 w-3.5" /> Campaign gallery
    </Link>
  );
}

function CampaignTable({
  rows,
  cycleId,
  galleryEnabled,
  ...sortProps
}: { rows: PortalComplianceCampaignRow[]; cycleId: number; galleryEnabled: boolean } & TableSortProps) {
  return (
    <table className={portalTableClass}>
      <thead className={portalTableHeadClass}>
        <tr>
          <th className="px-4 py-3"><SortButton label="Campaign" column="name" {...sortProps} /></th>
          <th className="px-4 py-3"><SortButton label="Stores" column="stores" {...sortProps} /></th>
          <th className="px-4 py-3"><SortButton label="Complete" column="complete" {...sortProps} /></th>
          <th className="px-4 py-3"><SortButton label="Progress" column="progress" {...sortProps} /></th>
          <th className="px-4 py-3"><SortButton label="Due" column="expected" {...sortProps} /></th>
          <th className="px-4 py-3"><SortButton label="Installed" column="installed" {...sortProps} /></th>
          <th className="px-4 py-3"><SortButton label="Shortfall" column="shortfall" {...sortProps} /></th>
          <th className="px-4 py-3">Status</th>
          {galleryEnabled ? <th className="px-4 py-3"><span className="sr-only">Campaign gallery</span></th> : null}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.campaignId} className="border-t border-border">
            <td className="px-4 py-3 font-medium text-foreground">{row.campaignName}</td>
            <td className="px-4 py-3">{row.totalStoreCount}</td>
            <td className="px-4 py-3">{row.storesWithAllInstalled}</td>
            <td className="min-w-[150px] px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="h-2 flex-1 rounded-full bg-muted">
                  <div className="h-2 rounded-full bg-primary" style={{ width: `${row.completionPercent}%` }} />
                </div>
                <span>{row.completionPercent}%</span>
              </div>
            </td>
            <td className="px-4 py-3">{row.expectedQty}</td>
            <td className="px-4 py-3">{row.actualQty}{row.unconfirmedQty > 0 && <div className="text-xs text-amber-700">{row.unconfirmedQty} unconfirmed</div>}</td>
            <td className="px-4 py-3 font-medium">{row.shortfallQty}</td>
            <td className="px-4 py-3"><StatusBadge status={row.status} /></td>
            {galleryEnabled ? (
              <td className="px-4 py-3 text-right">
                <EvidenceLink href={`/portal/gallery?cycleId=${cycleId}&campaignId=${row.campaignId}`} />
              </td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StoreTable({
  rows,
  cycleId,
  campaignId,
  galleryEnabled,
  ...sortProps
}: {
  rows: PortalComplianceStoreRow[];
  cycleId: number;
  campaignId?: number;
  galleryEnabled: boolean;
} & TableSortProps) {
  return (
    <table className={portalTableClass}>
      <thead className={portalTableHeadClass}>
        <tr>
          <th className="px-4 py-3"><SortButton label="Store" column="name" {...sortProps} /></th>
          <th className="px-4 py-3">Number</th>
          <th className="px-4 py-3"><SortButton label="Campaigns" column="campaigns" {...sortProps} /></th>
          <th className="px-4 py-3"><SortButton label="Install lines" column="lines" {...sortProps} /></th>
          <th className="px-4 py-3"><SortButton label="Due" column="expected" {...sortProps} /></th>
          <th className="px-4 py-3"><SortButton label="Installed" column="installed" {...sortProps} /></th>
          <th className="px-4 py-3"><SortButton label="Shortfall" column="shortfall" {...sortProps} /></th>
          <th className="px-4 py-3"><SortButton label="Status" column="status" {...sortProps} /></th>
          {galleryEnabled ? <th className="px-4 py-3"><span className="sr-only">Campaign gallery</span></th> : null}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const campaignQuery = campaignId ? `&campaignId=${campaignId}` : '';
          return (
            <tr key={row.storeId} className="border-t border-border">
              <td className="px-4 py-3 font-medium text-foreground">{row.storeName}</td>
              <td className="px-4 py-3 text-muted-foreground">{row.storeNumber ?? '—'}</td>
              <td className="px-4 py-3">{row.campaignCount}</td>
              <td className="px-4 py-3">{row.completedLineCount}/{row.lineCount}</td>
              <td className="px-4 py-3">{row.expectedQty}</td>
              <td className="px-4 py-3">
                {row.actualQty}
                {row.unconfirmedQty > 0 ? (
                  <div className="text-xs text-amber-700">{row.unconfirmedQty} unconfirmed</div>
                ) : null}
              </td>
              <td className="px-4 py-3 font-medium">{row.shortfallQty}</td>
              <td className="px-4 py-3"><StatusBadge status={row.status} /></td>
              {galleryEnabled ? (
                <td className="px-4 py-3 text-right">
                  <EvidenceLink href={`/portal/gallery?cycleId=${cycleId}&storeId=${row.storeId}${campaignQuery}`} />
                </td>
              ) : null}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function TaskTable({
  rows,
  cycleId,
  galleryEnabled,
  ...sortProps
}: { rows: PortalComplianceTaskRow[]; cycleId: number; galleryEnabled: boolean } & TableSortProps) {
  return (
    <table className={portalTableClass}>
      <thead className={portalTableHeadClass}>
        <tr>
          <th className="px-4 py-3"><SortButton label="Store" column="name" {...sortProps} /></th>
          <th className="px-4 py-3"><SortButton label="Campaign" column="campaign" {...sortProps} /></th>
          <th className="px-4 py-3"><SortButton label="Format" column="format" {...sortProps} /></th>
          <th className="px-4 py-3"><SortButton label="Due" column="expected" {...sortProps} /></th>
          <th className="px-4 py-3"><SortButton label="Installed" column="installed" {...sortProps} /></th>
          <th className="px-4 py-3"><SortButton label="Shortfall" column="shortfall" {...sortProps} /></th>
          <th className="px-4 py-3"><SortButton label="Status" column="status" {...sortProps} /></th>
          {galleryEnabled ? <th className="px-4 py-3"><span className="sr-only">Campaign gallery</span></th> : null}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key} className="border-t border-border">
            <td className="px-4 py-3">
              <div className="font-medium text-foreground">{row.storeName}</div>
              {row.storeNumber ? <div className="text-xs text-muted-foreground">#{row.storeNumber}</div> : null}
            </td>
            <td className="px-4 py-3">{row.campaignName}</td>
            <td className="px-4 py-3">
              {row.formatName}
              {row.exceptionNote ? (
                <div className="text-xs text-muted-foreground">{row.exceptionNote}</div>
              ) : null}
            </td>
            <td className="px-4 py-3">{row.expectedQty}</td>
            <td className="px-4 py-3">
              {row.actualQty}
              {row.unconfirmedQty > 0 ? (
                <div className="text-xs text-amber-700">Unconfirmed</div>
              ) : null}
            </td>
            <td className="px-4 py-3 font-medium">{row.shortfallQty}</td>
            <td className="px-4 py-3"><StatusBadge status={row.status} /></td>
            {galleryEnabled ? (
              <td className="px-4 py-3 text-right">
                <EvidenceLink href={`/portal/gallery?cycleId=${cycleId}&campaignId=${row.campaignId}&storeId=${row.storeId}`} />
              </td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
